import { weekdays } from '@/constants'
import { Contract, Order, SharedProps } from '@/types'
import { Head, Link, router } from '@inertiajs/react'
import {
  ActionIcon,
  Badge,
  Button,
  Collapse,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core'
import {
  IconArrowBackUp,
  IconChevronDown,
  IconChevronUp,
  IconCircleCheck,
  IconMapPin,
  IconPackage,
  IconPackages,
  IconPhone,
  IconPlus,
  IconRoute,
  IconTruck,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { ReactNode, useState } from 'react'

type Mode = 'collecte' | 'tournee' | 'depose'

type Stop = {
  order: Order
  contract: Contract
  action: { label: string; endpoint: string; color: string; variant: 'light' | 'filled'; icon: ReactNode }
  caption: string
}

type ZoneSummary = {
  id: number
  name: string
  service_days: number[]
  scheduled_today: boolean
}

interface Props extends SharedProps {
  pickups: Order[]
  collected: Order[]
  deliveries: Order[]
  zones: ZoneSummary[]
}

const groupBy = <T,>(items: T[], key: (item: T) => string): Record<string, T[]> =>
  items.reduce(
    (acc, item) => {
      const k = key(item)
      acc[k] = acc[k] ?? []
      acc[k].push(item)
      return acc
    },
    {} as Record<string, T[]>
  )

const pickupStop = (order: Order): Stop => ({
  order,
  contract: order.contract!,
  action: { label: 'Collecter', endpoint: 'collect', color: 'orange', variant: 'light', icon: <IconTruck size={14} /> },
  caption: `Confirmée le ${dayjs(order.dispatched_at).format('DD/MM/YYYY')}`,
})

const collectedStop = (order: Order): Stop => ({
  order,
  contract: order.contract!,
  action: {
    label: 'Annuler',
    endpoint: 'cancel_collect',
    color: 'red',
    variant: 'light',
    icon: <IconArrowBackUp size={14} />,
  },
  caption: `Collectée le ${dayjs(order.collected_at).format('DD/MM/YYYY')}`,
})

const deliveryStop = (order: Order): Stop => ({
  order,
  contract: order.contract!,
  action: {
    label: 'Livrer',
    endpoint: 'deliver',
    color: 'green',
    variant: 'filled',
    icon: <IconCircleCheck size={14} />,
  },
  caption: dayjs(order.created_at).format('DD/MM/YYYY'),
})

const emptyMessages: Record<Mode, string> = {
  tournee: 'Aucune commande à collecter pour le moment.',
  collecte: 'Aucune commande à collecter pour le moment.',
  depose: 'Aucune commande à déposer pour le moment.',
}

function EmptyState({ message }: { message: string }) {
  return (
    <Paper
      py={80}
      px="md"
      radius="lg"
      style={{ border: '1px dashed var(--mantine-color-gray-3)', textAlign: 'center' }}
    >
      <Stack align="center" gap="sm">
        <ThemeIcon size={64} radius="xl" variant="light" color="gray">
          <IconPackage size={32} />
        </ThemeIcon>
        <Title order={3}>Aucune commande</Title>
        <Text c="dimmed" size="sm">
          {message}
        </Text>
      </Stack>
    </Paper>
  )
}

const STORE_COLLAPSE_THRESHOLD = 5

function StoreCard({
  recipientName,
  items,
  onAction,
}: {
  recipientName: string
  items: Stop[]
  onAction: (orderId: number, endpoint: string) => void
}) {
  const { customer_address, customer_phone_number } = items[0].contract
  const [collapsed, setCollapsed] = useState(items.length > STORE_COLLAPSE_THRESHOLD)
  const toggleCollapsed = () => setCollapsed((value) => !value)

  return (
    <Paper withBorder radius="md" p="md" shadow="sm">
      <Group justify="space-between" wrap="nowrap" gap="sm" onClick={toggleCollapsed} style={{ cursor: 'pointer' }}>
        <Stack gap={4}>
          <Group gap="xs" wrap="nowrap">
            <Title order={4} size="h5">
              {recipientName}
            </Title>
            <Badge variant="light" color="gray" size="sm">
              {items.length}
            </Badge>
          </Group>
          {customer_address && (
            <Group gap={4} wrap="nowrap">
              <IconMapPin size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                {customer_address}
              </Text>
            </Group>
          )}
          {customer_phone_number && (
            <Group gap={4} wrap="nowrap">
              <IconPhone size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                {customer_phone_number}
              </Text>
            </Group>
          )}
        </Stack>
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label={collapsed ? 'Afficher les commandes' : 'Masquer les commandes'}
        >
          {collapsed ? <IconChevronDown size={18} /> : <IconChevronUp size={18} />}
        </ActionIcon>
      </Group>

      <Collapse in={!collapsed}>
        <Stack gap="xs" mt="md">
          {items.map(({ order, action, caption }) => (
            <Group
              key={order.id}
              justify="space-between"
              wrap="nowrap"
              py={8}
              style={{ borderTop: '1px solid var(--mantine-color-gray-1)' }}
            >
              <Stack gap={2}>
                <Text size="sm" fw={600}>
                  #{order.id} · {order.articles.length} articles
                </Text>
                <Text size="xs" c="dimmed">
                  {caption}
                </Text>
              </Stack>
              <Button
                size="xs"
                variant={action.variant}
                color={action.color}
                leftSection={action.icon}
                onClick={() => onAction(order.id, action.endpoint)}
              >
                {action.label}
              </Button>
            </Group>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  )
}

function StopList({ stops, onAction }: { stops: Stop[]; onAction: (orderId: number, endpoint: string) => void }) {
  const groupedStops = groupBy(stops, (stop) => stop.contract.customer_name)

  return (
    <Stack gap="md">
      {Object.entries(groupedStops).map(([recipientName, items]) => (
        <StoreCard key={recipientName} recipientName={recipientName} items={items} onAction={onAction} />
      ))}
    </Stack>
  )
}

function StopSection({
  icon,
  title,
  color,
  stops,
  onAction,
}: {
  icon: ReactNode
  title: string
  color: string
  stops: Stop[]
  onAction: (orderId: number, endpoint: string) => void
}) {
  return (
    <Stack gap="sm">
      <Group gap="xs" align="center">
        <ThemeIcon variant="light" color={color} size="sm" radius="sm">
          {icon}
        </ThemeIcon>
        <Text fw={700} size="sm">
          {title}
        </Text>
        <Badge color={color} variant="light" size="sm">
          {stops.length}
        </Badge>
      </Group>
      <StopList stops={stops} onAction={onAction} />
    </Stack>
  )
}

function ZoneSection({
  zone,
  stops,
  onAction,
}: {
  zone: ZoneSummary
  stops: Stop[]
  onAction: (orderId: number, endpoint: string) => void
}) {
  const color = zone.scheduled_today ? 'blue' : 'gray'
  const dayLabels = weekdays.filter((day) => zone.service_days.includes(day.value))

  return (
    <Stack gap="sm">
      <Group gap="xs" align="center" wrap="wrap">
        <ThemeIcon variant="light" color={color} size="sm" radius="sm">
          <IconRoute size={14} />
        </ThemeIcon>
        <Text fw={700} size="sm">
          {zone.name}
        </Text>
        <Group gap={4}>
          {dayLabels.map((day) => (
            <Badge key={day.value} size="xs" variant="light" color={color}>
              {day.label}
            </Badge>
          ))}
        </Group>
        <Badge color={color} variant="light" size="sm">
          {stops.length}
        </Badge>
      </Group>
      <StopList stops={stops} onAction={onAction} />
    </Stack>
  )
}

function CourierOrdersIndex({ pickups, collected, deliveries, zones, current_organization }: Props) {
  const [mode, setMode] = useState<Mode>('collecte')
  const [showAllZones, setShowAllZones] = useState(false)

  const post = (orderId: number, action: string) =>
    router.post(
      `/organizations/${current_organization!.slug}/couriers/orders/${orderId}/${action}`,
      {},
      { preserveScroll: true }
    )

  const handleAction = (orderId: number, endpoint: string) => {
    if (endpoint === 'cancel_collect' && !confirm('Annuler la collecte de cette commande ?')) return
    post(orderId, endpoint)
  }

  const pickupStops = pickups.map(pickupStop)
  const collectedStops = collected.map(collectedStop)
  const deliveryStops = deliveries.map(deliveryStop)

  const toggleShowAllZones = () => setShowAllZones((value) => !value)
  const stopsByZone = groupBy(pickupStops, (stop) => String(stop.contract.zone_id ?? 'none'))
  const zonesWithStops = zones.filter((zone) => (stopsByZone[String(zone.id)] ?? []).length > 0)
  const todayZones = zonesWithStops.filter((zone) => zone.scheduled_today)
  const otherZones = zonesWithStops.filter((zone) => !zone.scheduled_today)
  const noZoneStops = stopsByZone['none'] ?? []
  const hasHiddenZones = otherZones.length > 0 || noZoneStops.length > 0
  const todayStopCount = todayZones.reduce((total, zone) => total + stopsByZone[String(zone.id)].length, 0)

  let content
  if (mode === 'tournee') {
    content =
      pickupStops.length === 0 ? (
        <EmptyState message={emptyMessages.tournee} />
      ) : (
        <Stack gap="lg">
          {todayZones.length === 0 && !showAllZones && (
            <Text c="dimmed" size="sm" ta="center">
              Aucune zone programmée aujourd'hui.
            </Text>
          )}
          {todayZones.map((zone) => (
            <ZoneSection key={zone.id} zone={zone} stops={stopsByZone[String(zone.id)]} onAction={handleAction} />
          ))}
          {showAllZones &&
            otherZones.map((zone) => (
              <ZoneSection key={zone.id} zone={zone} stops={stopsByZone[String(zone.id)]} onAction={handleAction} />
            ))}
          {showAllZones && noZoneStops.length > 0 && (
            <StopSection
              icon={<IconMapPin size={14} />}
              title="Sans zone"
              color="gray"
              stops={noZoneStops}
              onAction={handleAction}
            />
          )}
          {hasHiddenZones && (
            <Button variant="subtle" onClick={toggleShowAllZones}>
              {showAllZones ? 'Afficher uniquement la tournée du jour' : 'Voir toutes les zones'}
            </Button>
          )}
        </Stack>
      )
  } else if (mode === 'depose') {
    content =
      deliveryStops.length === 0 ? (
        <EmptyState message={emptyMessages.depose} />
      ) : (
        <StopList stops={deliveryStops} onAction={handleAction} />
      )
  } else if (pickupStops.length === 0 && collectedStops.length === 0) {
    content = <EmptyState message={emptyMessages.collecte} />
  } else {
    content = (
      <Stack gap="lg">
        {pickupStops.length > 0 && (
          <StopSection
            icon={<IconTruck size={14} />}
            title="À collecter"
            color="orange"
            stops={pickupStops}
            onAction={handleAction}
          />
        )}
        {collectedStops.length > 0 && (
          <StopSection
            icon={<IconPackages size={14} />}
            title="Collectées"
            color="gray"
            stops={collectedStops}
            onAction={handleAction}
          />
        )}
      </Stack>
    )
  }

  return (
    <Stack gap="xl">
      <Head title={`Mes Commandes - ${current_organization!.name}`} />

      <Group justify="space-between" align="center">
        <Title>Mes Commandes</Title>
        <Button
          component={Link}
          href={`/organizations/${current_organization!.slug}/couriers/orders/new`}
          leftSection={<IconPlus size={18} />}
        >
          Nouvelle commande
        </Button>
      </Group>

      <SegmentedControl
        fullWidth
        size="md"
        value={mode}
        onChange={(value) => setMode(value as Mode)}
        data={[
          { value: 'collecte', label: pickups.length ? `Collecte (${pickups.length})` : 'Collecte' },
          { value: 'tournee', label: todayStopCount ? `Tournée (${todayStopCount})` : 'Tournée' },
          { value: 'depose', label: deliveries.length ? `Dépose (${deliveries.length})` : 'Dépose' },
        ]}
      />

      {content}
    </Stack>
  )
}

export default CourierOrdersIndex
